
import { parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { FormData } from "@/types/inventory";
import { toast } from "sonner";

export const validateItemForm = (formData: FormData): Record<string, string> => {
  return {
    name: !formData.name ? "Item name is required" : "",
    quantity: formData.quantity < 1 ? "Quantity must be at least 1" : "",
    category: !formData.category ? "Category is required" : "",
    expiration: !formData.expiration ? "Expiration date is required" : "",
  };
};

export const formatExpirationDate = (expiration: Date | string): string | null => {
  if (!expiration) return null;
  
  if (expiration instanceof Date) {
    return expiration.toISOString().split('T')[0];
  } else if (typeof expiration === 'string') {
    const expirationString = expiration as string;
    if (expirationString && expirationString.includes('T')) {
      // It's an ISO date string
      return parseISO(expirationString).toISOString().split('T')[0];
    } else {
      // Assume it's already in YYYY-MM-DD format
      return expirationString;
    }
  }
  
  return null;
};

export const saveItemOffline = (item: FormData): void => {
  const offlineItems = JSON.parse(localStorage.getItem('offlineInventoryItems') || '[]');
  offlineItems.push({
    ...item,
    pendingSince: new Date().toISOString()
  });
  localStorage.setItem('offlineInventoryItems', JSON.stringify(offlineItems));
  
  toast.success("Item saved offline. Will sync when you're back online.", {
    position: "bottom-right",
    duration: 3000,
  });
};

export const saveItemToDatabase = async (
  item: FormData, 
  userId: string | undefined
): Promise<any> => {
  const expiration = formatExpirationDate(item.expiration);
  
  // Extract barcode from notes if it exists
  let barcode = null;
  const qrCodeRegex = /QR Code: ([^\n]+)/;
  const match = item.notes?.match(qrCodeRegex);
  
  if (match && match[1]) {
    barcode = match[1];
  }
  
  const sanitizedData = {
    name: String(item.name).trim(),
    description: item.notes ? String(item.notes).trim() : null,
    category: String(item.category).trim(),
    quantity: Math.max(1, Number(item.quantity)),
    has_expiry: Boolean(expiration),
    expiry_date: expiration,
    location: item.location ? String(item.location).trim() : null,
    image_url: item.image_url || null,
    barcode: barcode, // Save the QR code as barcode
    user_id: userId
  };
  
  console.log("Saving item to database with data:", sanitizedData);
  
  const { data, error } = await supabase
    .from('inventory_items')
    .insert(sanitizedData)
    .select();
  
  if (error) {
    console.error("Error saving item to database:", error);
    throw error;
  }
  
  console.log("Item saved successfully:", data?.[0]);
  return data?.[0];
};

export const checkForDuplicateItems = async (
  data: FormData,
  userId: string | undefined
): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    const { data: items, error } = await supabase
      .from('inventory_items')
      .select('id, name, quantity')
      .eq('user_id', userId)
      .eq('name', data.name)
      .eq('category', data.category)
      .limit(1);
      
    if (error) throw error;
    
    return items && items.length > 0;
  } catch (error) {
    console.error("Error checking for duplicates:", error);
    return false;
  }
};
